import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save
} from 'lucide-react';
import { eventService } from '@/services/eventService';
import { CreateEventDto, UpdateEventDto, EVENT_CATEGORIES, EVENT_PRICE_TYPES, EVENT_STATUSES } from '@/types/event';
import { useCafeteriaSelection } from '../../../context/CafeteriaSelectionContext';
import { cityService, City } from '../../../services/api';
import ImageUpload from '../../../components/ui/ImageUpload';

export default function EventFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { currentCafeteria } = useCafeteriaSelection();

  const [formData, setFormData] = useState<CreateEventDto>({
    title: '',
    description: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    location: '',
    locationAddress: '',
    imageUrl: '',
    price: 0,
    priceType: 1, // Free
    category: 1, // Workshop
    maxAttendees: undefined,
    organizer: currentCafeteria?.name || '', // Preenche automaticamente com o nome da cafeteria
    organizerAvatar: '',
    registrationUrl: '',
    placeId: currentCafeteria?.id || '', // Preenche automaticamente com o ID da cafeteria
    tags: [],
    requirements: [],
    includes: []
  });

  const [status, setStatus] = useState(1); // Active
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [requirementInput, setRequirementInput] = useState('');
  const [includeInput, setIncludeInput] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    loadCities();
    if (isEdit && id) {
      loadEvent(id);
    }
  }, [isEdit, id]);

  // Atualizar PlaceId e Organizador quando a cafeteria mudar
  useEffect(() => {
    if (currentCafeteria && !isEdit) {
      setFormData(prev => ({
        ...prev,
        placeId: currentCafeteria.id,
        organizer: currentCafeteria.name
      }));
    }
  }, [currentCafeteria, isEdit]);

  const loadCities = async () => {
    try {
      const citiesData = await cityService.getAllCities();
      setCities(citiesData);
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
    }
  };

  const loadEvent = async (eventId: string) => {
    try {
      setLoading(true);
      const event = await eventService.getEventById(eventId);
      if (event) {
        // Backend handles permission validation
        setFormData({
          title: event.title,
          description: event.description,
          eventDate: event.eventDate.toISOString().split('T')[0],
          startTime: event.startTime,
          endTime: event.endTime,
          location: event.location,
          locationAddress: event.locationAddress,
          imageUrl: event.imageUrl,
          price: event.price,
          priceType: event.priceType === 'free' ? 1 : 2,
          category: EVENT_CATEGORIES.find(c => c.label.toLowerCase().replace(' ', '_') === event.category)?.value || 1,
          maxAttendees: event.maxAttendees,
          organizer: event.organizer,
          organizerAvatar: event.organizerAvatar || '',
          registrationUrl: event.registrationUrl || '',
          placeId: event.placeId || '',
          cityId: event.cityId || '',
          tags: event.tags,
          requirements: event.requirements || [],
          includes: event.includes
        });
        setStatus(event.status === 'active' ? 1 : event.status === 'cancelled' ? 2 : 3);
      }
    } catch (error) {
      console.error('Erro ao carregar evento:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      if (isEdit && id) {
        const updateData: UpdateEventDto = {
          ...formData,
          id,
          status
        };
        await eventService.updateEvent(id, updateData);
      } else {
        await eventService.createEvent(formData);
      }
      
      navigate('/cafeteria/events');
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      alert('Erro ao salvar evento');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof CreateEventDto, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addRequirement = () => {
    if (requirementInput.trim() && !formData.requirements?.includes(requirementInput.trim())) {
      setFormData(prev => ({
        ...prev,
        requirements: [...(prev.requirements || []), requirementInput.trim()]
      }));
      setRequirementInput('');
    }
  };

  const removeRequirement = (requirementToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements?.filter(req => req !== requirementToRemove) || []
    }));
  };

  const addInclude = () => {
    if (includeInput.trim() && !formData.includes.includes(includeInput.trim())) {
      setFormData(prev => ({
        ...prev,
        includes: [...prev.includes, includeInput.trim()]
      }));
      setIncludeInput('');
    }
  };

  const removeInclude = (includeToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      includes: prev.includes.filter(inc => inc !== includeToRemove)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/cafeteria/events')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Editar Evento' : 'Novo Evento'}
          </h1>
          <p className="text-gray-600">
            {isEdit ? 'Atualize as informações do evento' : 'Preencha as informações do novo evento'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Básicas</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título do Evento *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Workshop de Latte Art"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Descreva o evento em detalhes..."
                  />
                </div>

              {/* Campo de categoria comentado - mantido no backend para uso futuro */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {EVENT_CATEGORIES.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Preço *
                  </label>
                  <select
                    required
                    value={formData.priceType}
                    onChange={(e) => handleInputChange('priceType', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {EVENT_PRICE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.priceType === 2 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preço (R$) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required={formData.priceType === 2}
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Date and Time */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Data e Horário</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data do Evento *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.eventDate}
                    onChange={(e) => handleInputChange('eventDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Início *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.startTime}
                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Fim *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Localização</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <select
                    value={formData.cityId || ''}
                    onChange={(e) => handleInputChange('cityId', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione uma cidade</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Local *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Café Central"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço Completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.locationAddress}
                    onChange={(e) => handleInputChange('locationAddress', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Rua das Flores, 123 - Centro, São Paulo - SP"
                  />
                </div>
              </div>
            </div>

            {/* Organizer - Hidden for cafeteria users */}
            {/* A cafeteria é automaticamente o organizador do evento */}

            {/* Additional Information */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Adicionais</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL de Inscrição
                  </label>
                  <input
                    type="url"
                    value={formData.registrationUrl}
                    onChange={(e) => handleInputChange('registrationUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://eventbrite.com/evento"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Imagem do Evento *
                  </label>
                  <ImageUpload
                    value={formData.imageUrl}
                    onChange={(url) => {
                      handleInputChange('imageUrl', url);
                      setUploadError(null);
                    }}
                    onError={(error) => setUploadError(error)}
                    className="w-full"
                    maxSize={10} // 10MB para imagens de eventos
                  />
                  {uploadError && (
                    <p className="mt-1 text-sm text-red-600">{uploadError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Máximo de Participantes
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxAttendees || ''}
                    onChange={(e) => handleInputChange('maxAttendees', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 30 (deixe vazio para ilimitado)"
                  />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tags</h2>
              
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite uma tag e pressione Enter"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="btn btn-primary"
                  >
                    Adicionar
                  </button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Requisitos</h2>
              
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={requirementInput}
                    onChange={(e) => setRequirementInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite um requisito e pressione Enter"
                  />
                  <button
                    type="button"
                    onClick={addRequirement}
                    className="btn btn-primary"
                  >
                    Adicionar
                  </button>
                </div>
                
                <div className="space-y-2">
                  {formData.requirements?.map((requirement, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm text-gray-700">{requirement}</span>
                      <button
                        type="button"
                        onClick={() => removeRequirement(requirement)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Includes */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">O que está incluso</h2>
              
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={includeInput}
                    onChange={(e) => setIncludeInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclude())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite um item incluso e pressione Enter"
                  />
                  <button
                    type="button"
                    onClick={addInclude}
                    className="btn btn-primary"
                  >
                    Adicionar
                  </button>
                </div>
                
                <div className="space-y-2">
                  {formData.includes.map((include, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm text-gray-700">{include}</span>
                      <button
                        type="button"
                        onClick={() => removeInclude(include)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            {isEdit && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
                <select
                  value={status}
                  onChange={(e) => setStatus(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {EVENT_STATUSES.map(statusOption => (
                    <option key={statusOption.value} value={statusOption.value}>
                      {statusOption.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Ações</h2>
              
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="btn btn-primary w-full flex items-center justify-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Salvando...' : isEdit ? 'Atualizar Evento' : 'Criar Evento'}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate('/cafeteria/events')}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
